// src/components/PublicCalendar.jsx
// Mostrar horarios en la zona horaria del VISITANTE (paciente).
// Si los slots vienen sin TZ, intentamos convertir usando la TZ del médico (si el endpoint la trae);
// de lo contrario, asumimos UTC como fallback.
// Botones y chip en top. Ajustes mobile-friendly, scroll horizontal SOLO mobile.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Button,
  Fade,
  Paper,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import axiosInstance from '../axiosInstance';
import BookingForm from './BookingForm';
import moment from 'moment-timezone';
import momentPlugin from '@fullcalendar/moment';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';

const isIsoWithZone = (s) => /Z$|[+-]\d{2}:\d{2}$/.test(s || '');
const isValidIana = (tz) => {
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch { return false; }
};

// Normaliza strings comunes a IANA válidas; si no, null
const normalizeTz = (tz) => {
  if (!tz || typeof tz !== 'string') return null;
  const t = tz.trim();
  if (isValidIana(t)) return t;

  const m = t.match(/^(UTC|GMT)\s*([+-])\s*(\d{1,2})(?::(\d{2}))?$/i);
  if (m) {
    const sign = m[2] === '+' ? '-' : '+'; // IANA invierte signo
    const hh = parseInt(m[3], 10);
    const candidate = `Etc/GMT${sign}${hh}`;
    return isValidIana(candidate) ? candidate : null;
  }
  const map = {
    CST: 'America/Tegucigalpa',
    'UTC-6': 'Etc/GMT+6',
    'GMT-6': 'Etc/GMT+6',
    'Central Standard Time': 'America/Tegucigalpa',
  };
  return isValidIana(map[t]) ? map[t] : null;
};

const PublicCalendar = ({ doctorId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const calendarRef = useRef(null);

  // Zona horaria del visitante (paciente)
  const visitorTz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC';
  const calendarKey = visitorTz; // Forzar remonte si cambia

  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [dateRangeText, setDateRangeText] = useState('');
  const [currentView, setCurrentView] = useState(isMobile ? 'timeGridDay' : 'timeGridWeek');
  const [showScrollHelper, setShowScrollHelper] = useState(true);
  const [isPaying, setIsPaying] = useState(false);

  const abortRef = useRef(null);

  // Cargar slots de disponibilidad en el rango visible
  const fetchAvailableSlots = useCallback(
    async (fetchInfo) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const params = {
        start: fetchInfo.startStr,
        end: fetchInfo.endStr,
      };

      // Construye eventos para FullCalendar y etiqueta de tiempos en la TZ del visitante
      const mapSlotsToEvents = (slots, doctorTzMaybe) => {
        const doctorTz = normalizeTz(doctorTzMaybe) || null;

        return (slots || []).map((slot) => {
          let startIso = slot.start;
          let endIso = slot.end;

          // 1) Si no traen zona horaria, convertir
          if (!isIsoWithZone(startIso)) {
            if (doctorTz) {
              // Interpretar como hora LOCAL del médico y convertir a UTC
              startIso = moment.tz(startIso, doctorTz).toISOString();
            } else {
              // Fallback: asumir UTC
              startIso = `${startIso}Z`;
            }
          }
          if (!isIsoWithZone(endIso)) {
            if (doctorTz) {
              endIso = moment.tz(endIso, doctorTz).toISOString();
            } else {
              endIso = `${endIso}Z`;
            }
          }

          const startDate = new Date(startIso);
          const endDate = new Date(endIso);

          // 2) Texto visible en TZ del visitante
          const fmt = new Intl.DateTimeFormat('es-ES', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: visitorTz
          });
          const formattedTime = `${fmt.format(startDate)} - ${fmt.format(endDate)}`;

          return {
            start: startIso, // UTC o con offset
            end: endIso,
            title: 'Disponible',
            backgroundColor: theme.palette.success.main,
            borderColor: theme.palette.success.dark,
            textColor: '#ffffff',
            display: 'block',
            classNames: ['available-slot'],
            extendedProps: {
              isAvailable: true,
              rawStart: slot.start,
              rawEnd: slot.end,
              formattedTime,
            },
          };
        });
      };

      try {
        setLoading(true);
        setError(null);

        // Endpoint recomendado: puede traer timezone (del médico)
        const res = await axiosInstance.get(`/doctors/${doctorId}/available/`, {
          params,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        const { slots = [], timezone = null } = res.data || {};
        const events = mapSlotsToEvents(slots, timezone);
        setAvailableSlots(events);
        setError(null);
      } catch (err) {
        if (
          err?.name === 'CanceledError' ||
          err?.name === 'AbortError' ||
          err?.code === 'ERR_CANCELED' ||
          controller.signal.aborted
        ) {
          return;
        }

        // Fallback legacy
        const status = err?.response?.status;
        if (status !== 404 && status !== 405) {
          setError('No se pudieron cargar los horarios disponibles. Por favor, intenta nuevamente.');
          setAvailableSlots([]);
          return;
        }

        try {
          const resOld = await axiosInstance.get(`/doctors/${doctorId}/available-slots/`, {
            params,
            signal: controller.signal,
          });

          if (controller.signal.aborted) return;

          const data = resOld.data ?? [];
          const slotsArray = Array.isArray(data) ? data : (Array.isArray(data.slots) ? data.slots : []);
          const tzRaw = (data && typeof data === 'object' && (data.timezone || data.zona_horaria || data.time_zone)) || null;
          const events = mapSlotsToEvents(slotsArray, tzRaw);
          setAvailableSlots(events);
          setError(null);
        } catch (err2) {
          if (
            err2?.name === 'CanceledError' ||
            err2?.name === 'AbortError' ||
            err2?.code === 'ERR_CANCELED' ||
            controller.signal.aborted
          ) {
            return;
          }
          setError('No se pudieron cargar los horarios disponibles. Por favor, intenta nuevamente.');
          setAvailableSlots([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [doctorId, theme.palette, visitorTz]
  );

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const handleDatesSet = (dateInfo) => {
    fetchAvailableSlots(dateInfo);
    setDateRangeText(formatDateRange(dateInfo.start, dateInfo.end));
    setCurrentView(dateInfo.view.type);
  };

  const handleEventClick = (info) => {
    if (info.event.extendedProps.isAvailable) {
      setSelectedSlot({
        start: info.event.start,
        end: info.event.end,
        rawStart: info.event.extendedProps.rawStart,
        rawEnd: info.event.extendedProps.rawEnd,
      });
      setShowBookingForm(true);
    }
  };

  const handleBookingSuccess = () => {
    setShowBookingForm(false);
    setSelectedSlot(null);
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      const view = api.view;
      fetchAvailableSlots({
        start: view.activeStart,
        end: view.activeEnd,
        startStr: view.activeStart.toISOString(),
        endStr: view.activeEnd.toISOString(),
      });
    }
  };

  const handleBookingCancel = () => {
    setShowBookingForm(false);
    setSelectedSlot(null);
  };

  const formatDateRange = (start, end) => {
    const monthNames = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
    ];

    if (currentView === 'dayGridMonth') {
      if (calendarRef.current) {
        try {
          const api = calendarRef.current.getApi();
          const currentDate = api.getDate();
          const monthName = monthNames[currentDate.getMonth()];
          return `${monthName} de ${currentDate.getFullYear()}`;
        } catch {
          const monthName = monthNames[start.getMonth()];
          return `${monthName} de ${start.getFullYear()}`;
        }
      } else {
        const monthName = monthNames[start.getMonth()];
        return `${monthName} de ${start.getFullYear()}`;
      }
    } else if (currentView === 'timeGridDay') {
      const day = start.getDate();
      const monthName = monthNames[start.getMonth()];
      return `${day} de ${monthName} de ${start.getFullYear()}`;
    } else {
      const startDay = start.getDate();
      const endDate = new Date(end);
      endDate.setDate(endDate.getDate() - 1);
      const endDay = endDate.getDate();
      const startMonthName = monthNames[start.getMonth()];
      const endMonthName = monthNames[endDate.getMonth()];
      if (start.getMonth() === endDate.getMonth()) {
        return `${startDay} - ${endDay} de ${startMonthName} de ${start.getFullYear()}`;
      } else {
        return `${startDay} ${startMonthName} - ${endDay} ${endMonthName} de ${start.getFullYear()}`;
      }
    }
  };

  useEffect(() => {
    setFadeIn(true);
    if (isMobile) {
      const timer = setTimeout(() => setShowScrollHelper(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  // tamaños para el contenido de cada slot (para no depender de inline grandes en mobile)
  const slotTimeSize = isMobile ? '0.9rem' : '1.08rem';
  const slotTitleSize = isMobile ? '0.85rem' : '1.02rem';

  return (
    <Fade in={fadeIn} timeout={800}>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <style>{`
          .fc { display: flex; flex-direction: column; height: 100%; font-family: ${theme.typography.fontFamily}; }
          .fc-view-harness { margin-top: 10px !important; flex-grow: 1; overflow: auto !important; -webkit-overflow-scrolling: touch !important; background-color: ${theme.palette.background.paper}; border-radius: 20px; border: 2px solid ${alpha(theme.palette.divider, 0.2)}; box-shadow: 0 8px 32px ${alpha(theme.palette.common.black, 0.1)}; scrollbar-width: none; -ms-overflow-style: none; }
          .fc-view-harness::-webkit-scrollbar { width: 0; height: 0; display: none; }
          .fc-toolbar-title { visibility: hidden !important; height: 0 !important; overflow: hidden !important; position: absolute !important; }

          /* Header de días (escritorio) */
          .fc-col-header-cell { background: linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.12)} 100%); border: none !important; padding: 16px 8px !important; }
          .fc-col-header-cell-cushion { font-size: 1.3rem !important; font-weight: 800 !important; text-transform: uppercase; color: ${theme.palette.primary.dark}; padding: 16px 8px !important; display: block !important; text-decoration: none !important; }

          /* Toolbar original oculta y reemplazada por la personalizada */
          .fc .fc-toolbar.fc-header-toolbar { height: 60px !important; min-height: 60px !important; padding-top: 70px !important; margin-bottom: 1em !important; padding: 0 16px !important; position: relative !important; background-color: ${alpha(theme.palette.primary.light, 0.05)}; border-radius: 16px 16px 0 0; border-bottom: 1px solid ${alpha(theme.palette.divider, 0.1)}; display: flex !important; align-items: center !important; }
          .fc-toolbar-chunk { visibility: hidden !important; position: absolute !important; width: 0 !important; height: 0 !important; overflow: hidden !important; }

          /* Hover para slots disponibles (verde) */
          .fc .available-slot {
            cursor: pointer;
            border: none !important;
            border-radius: 12px !important;
            transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
          }
          .fc .available-slot:hover {
            transform: translateY(-3px) scale(1.02);
            box-shadow: 0 8px 22px rgba(46, 204, 113, 0.35);
            filter: brightness(1.05);
          }
          .fc .available-slot .fc-event-main { transition: inherit; }
          .fc .available-slot:focus-visible {
            outline: 3px solid rgba(46, 204, 113, 0.6);
            outline-offset: 2px;
          }

          /* Botones personalizados (desktop top: 76px) */
          .custom-nav-buttons-container { position: absolute !important; top: 76px !important; left: 16px !important; z-index: 10 !important; display: flex !important; gap: 8px !important; }
          .custom-view-buttons-container { position: absolute !important; top: 76px !important; right: 16px !important; z-index: 10 !important; display: flex !important; gap: 8px !important; }
          .custom-calendar-button { height: 40px !important; min-width: 40px !important; padding: 0 14px !important; display: flex !important; align-items: center !important; justify-content: center !important; text-transform: capitalize !important; font-weight: 700 !important; box-shadow: 0 4px 12px ${alpha(theme.palette.primary.main, 0.2)} !important; font-size: 1rem !important; border-radius: 18px !important; transition: all 0.3s ease !important; border: none !important; letter-spacing: 0.3px; background: linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%) !important; color: white !important; cursor: pointer !important; }
          .custom-calendar-button:hover { box-shadow: 0 6px 20px ${alpha(theme.palette.primary.main, 0.35)} !important; transform: translateY(-3px) !important; background: linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%) !important; }
          .custom-calendar-button.active { background: linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(theme.palette.primary.dark, 0.8)} 100%) !important; box-shadow: 0 2px 8px ${alpha(theme.palette.common.black, 0.3)} inset !important; transform: translateY(0px) !important; }

          /* Chip de rango centrado (desktop top: 76px) */
          .date-range-chip-container { position: absolute !important; top: 76px !important; left: 50% !important; transform: translateX(-50%) !important; z-index: 9 !important; pointer-events: none !important; }

          /* Celdas y fuentes (escritorio) */
          .fc-dayGridMonth-view .fc-daygrid-day { min-height: 180px !important; }
          .fc-dayGridMonth-view .fc-daygrid-day-number { font-size: 1.25rem !important; font-weight: 800 !important; padding: 10px 12px !important; }
          .fc-dayGridMonth-view .fc-daygrid-day-events { min-height: 100px !important; padding: 6px !important; }

          .fc-timegrid-slot { height: 76px !important; border-color: ${alpha(theme.palette.divider, 0.15)} !important; }
          .fc-timegrid-slot-label { font-weight: 800 !important; color: ${theme.palette.text.secondary}; font-size: 1.1rem !important; }
          .fc-timegrid-axis-cushion { font-size: 1.1rem !important; font-weight: 800 !important; }

          .fc-timegrid-now-indicator-line { border-color: ${theme.palette.error.main}; border-width: 3px; box-shadow: 0 0 12px ${alpha(theme.palette.error.main, 0.7)}; }
          .fc-timegrid-now-indicator-arrow { border-color: ${theme.palette.error.main}; border-width: 6px; filter: drop-shadow(0 0 4px ${alpha(theme.palette.error.main, 0.7)}); }

          .no-slots-overlay { position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%); z-index: 8; }

          /* SOLO móviles */
          @media (max-width: 600px) {
            .fc-view-harness { margin-top: 4px !important; } /* antes 10px */
            /* Reducir espacio reservado en el toolbar original y bajar botones bastante */
            .fc .fc-toolbar.fc-header-toolbar {
              padding-top: 72px !important; /* antes 50px */
              height: 34px !important;
              min-height: 34px !important;
              margin-bottom: 6px !important;
              padding: 0 6px !important;
              border-radius: 12px !important;
            }

            /* Botones y chip más abajo y más cerca de los bordes */
            .custom-nav-buttons-container,
            .custom-view-buttons-container,
            .date-range-chip-container {
              top: 94px !important; /* antes 56px */
            }
            .custom-nav-buttons-container { left: 8px !important; gap: 4px !important; }
            .custom-view-buttons-container { right: 8px !important; gap: 4px !important; }

            /* Botones AÚN pequeños en móvil (manteniendo efectos/colores) */
            .custom-calendar-button {
              height: 20px !important;
              min-width: 20px !important;
              padding: 0 1px !important;
              font-size: 0.4rem !important;
              border-radius: 7px !important;
              box-shadow: 0 2px 6px rgba(25, 118, 210, 0.2) !important; /* alpha(theme.palette.primary.main, 0.2) */
            }
            /* Flechas redondas mini */
            .custom-nav-buttons-container .custom-calendar-button {
              width: 18px !important;
              min-width: 18px !important;
              padding: 0 !important;
              border-radius: 50% !important;
            }

            /* Chip más pequeño */
            .date-range-chip-container { top: 90px !important; }
            .date-range-chip-container .MuiChip-root {
              width: 90px !important;
              min-width: 90px !important;
              height: 22px !important;
              min-height: 24px !important;
              padding: 3px 5px !important;
              border-radius: 12px !important;
              box-shadow: 0 2px 6px rgba(0,0,0,0.12) !important;
            }
            .date-range-chip-container .MuiChip-label {
              font-size: 0.33rem !important;
              padding: 0 8px !important;
              font-weight: 700 !important;
            }
            .date-range-chip-container .MuiChip-icon {
              margin-left: 1px !important;
              width: 10px !important;
              height: 10px !important;
            }

            /* Encabezado de días más pequeño (formato intacto) */
            .fc-col-header-cell { padding: 6px 4px !important; }
            .fc-col-header-cell-cushion {
              font-size: 0.8rem !important;
              font-weight: 700 !important;
              text-transform: none !important;
              letter-spacing: 0 !important;
              padding: 6px 4px !important;
            }

            /* Slots y eje de horas más compactos y nítidos (sin cortes) */
            .fc-timegrid-slot { height: 48px !important; }
            .fc-timegrid-axis { min-width: 56px !important; width: 56px !important; }
            .fc-timegrid-slot-label-cushion,
            .fc-timegrid-axis-cushion {
              font-size: 0.78rem !important;
              padding: 4px 4px !important;
              font-weight: 800 !important;
              text-shadow: none !important;
              color: ${/* theme.palette.text.primary via CSS no es posible, pero mantenemos color por defecto */''}inherit !important;
            }

            /* Scroll horizontal SOLO en mobile: wrapper interno */
            .public-calendar-x-wrap {
              overflow-x: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }
            .public-calendar-x-wrap .fc-view-harness { overflow-x: visible !important; }
            .public-calendar-x-wrap .fc .fc-scroller-harness .fc-scroller { overflow-x: visible !important; }
            .public-calendar-x-wrap .fc-timeGridWeek-view .fc-scrollgrid,
            .public-calendar-x-wrap .fc-timeGridWeek-view .fc-timegrid-body,
            .public-calendar-x-wrap .fc-timeGridWeek-view .fc-scrollgrid-sync-table,
            .public-calendar-x-wrap .fc-timeGridWeek-view .fc-col-header,
            .public-calendar-x-wrap .fc-timeGridWeek-view .fc-col-header table {
              min-width: 980px !important;
            }

            /* Vista mensual algo más compacta */
            .fc-dayGridMonth-view .fc-daygrid-day { min-height: 150px !important; }
          }
          /* Solo vista mensual: tamaño del texto de los días (Lun, Mar, Mié...) */
          .fc-dayGridMonth-view .fc-col-header-cell-cushion {
            font-size: 0.38rem !important;     /* ajusta a tu gusto (ej. 0.85rem, 0.8rem) */
            font-weight: 700 !important;
            text-transform: none !important;
            padding: 8px 0px !important;      /* opcional: menos padding */
          }  
        `}</style>

        {/* Aviso superior */}
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          Haz clic en un horario disponible (en verde) para agendar tu cita.
        </Alert>

        {/* Botones navegación */}
        <Box className="custom-nav-buttons-container">
          <Button className="custom-calendar-button" onClick={() => calendarRef.current?.getApi().prev()} sx={{ minWidth: '40px', width: '40px', padding: 0, borderRadius: '50%' }}>
            <ArrowBackIcon fontSize="small" />
          </Button>
          <Button className="custom-calendar-button" onClick={() => calendarRef.current?.getApi().next()} sx={{ minWidth: '40px', width: '40px', padding: 0, borderRadius: '50%' }}>
            <ArrowForwardIcon fontSize="small" />
          </Button>
          <Button className="custom-calendar-button" onClick={() => calendarRef.current?.getApi().today()} sx={{ ml: 1 }}>
            Hoy
          </Button>
        </Box>

        {/* Botones de vista */}
        <Box className="custom-view-buttons-container">
          <Button
            variant="contained"
            color="primary"
            className={`custom-calendar-button month-button ${currentView === 'dayGridMonth' ? 'active' : ''}`}
            onClick={() => calendarRef.current?.getApi().changeView('dayGridMonth')}
          >
            Mes
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={`custom-calendar-button week-button ${currentView === 'timeGridWeek' ? 'active' : ''}`}
            onClick={() => calendarRef.current?.getApi().changeView('timeGridWeek')}
          >
            Semana
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={`custom-calendar-button day-button ${currentView === 'timeGridDay' ? 'active' : ''}`}
            onClick={() => calendarRef.current?.getApi().changeView('timeGridDay')}
          >
            Día
          </Button>
        </Box>

        {/* Chip de rango */}
        <Box className="date-range-chip-container">
          <Chip
            icon={<CalendarTodayIcon />}
            label={dateRangeText}
            color="primary"
            sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              px: 2,
              py: 2,
              borderRadius: '30px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              pointerEvents: 'auto',
              minWidth: '240px',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              },
              '& .MuiChip-label': {
                px: 1,
                color: 'white',
                fontWeight: 700,
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
              },
              '& .MuiChip-icon': { color: 'white', ml: '8px' },
            }}
          />
        </Box>

        {/* Overlay de carga */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 130,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 7,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Overlay de error */}
        {error && !loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 130,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 7,
              maxWidth: 560,
              width: '90%',
            }}
          >
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              {error}
            </Alert>
          </Box>
        )}

        {/* Overlay sin horarios */}
        {!loading && !error && availableSlots.length === 0 && (
          <Box className="no-slots-overlay">
            <Alert
              severity="info"
              sx={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                bgcolor: alpha(theme.palette.info.light, 0.2), 
                backdropFilter: 'blur(2px)',
              }}
            >
              No hay horarios disponibles en el rango seleccionado. Prueba navegando a otra semana para verificar disponibilidad.
            </Alert>
          </Box>
        )}

        {/* Calendario con wrapper para scroll horizontal SOLO mobile */}
        <Box className="public-calendar-x-wrap" sx={{ height: '100%', width: '100%' }}>
          <FullCalendar
            key={calendarKey}
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, momentPlugin, momentTimezonePlugin]}
            initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
            headerToolbar={{ left: ' ', center: ' ', right: ' ' }}
            firstDay={1}
            locale={esLocale}
            buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }}
            events={availableSlots}
            eventClick={handleEventClick}
            eventDidMount={(info) => {
              if (info.event.extendedProps?.isAvailable && info.el) {
                info.el.title = `${info.event.extendedProps.formattedTime} · Clic para agendar`;
              }
            }}
            height="100%"
            allDaySlot={false}
            slotMinTime="07:00:00"
            slotMaxTime="23:00:00"
            nowIndicator
            timeZone={isValidIana(visitorTz) ? visitorTz : 'local'}
            scrollTime="08:00:00"
            expandRows
            slotEventOverlap={false}
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            slotLabelFormat={
              isMobile
                ? { hour: '2-digit', minute: '2-digit', hour12: false, omitZeroMinute: false } // 09:00, 10:00
                : { hour: '2-digit', minute: '2-digit', hour12: false }
            }
            dayHeaderFormat={{ weekday: 'long', day: 'numeric', omitCommas: true }}
            titleFormat={{ month: 'long', year: 'numeric' }}
            slotDuration="00:30:00"
            stickyHeaderDates
            handleWindowResize
            contentHeight="auto"
            datesSet={(dateInfo) => {
              handleDatesSet(dateInfo);
              // Activar clases 'active' en botones
              setTimeout(() => {
                const monthButton = document.querySelector('.month-button');
                const weekButton = document.querySelector('.week-button');
                const dayButton = document.querySelector('.day-button');
                if (monthButton && weekButton && dayButton) {
                  monthButton.classList.toggle('active', dateInfo.view.type === 'dayGridMonth');
                  weekButton.classList.toggle('active', dateInfo.view.type === 'timeGridWeek');
                  dayButton.classList.toggle('active', dateInfo.view.type === 'timeGridDay');
                }
              }, 50);
            }}
            // Render personalizado del slot (ajustado con tamaños responsivos)
            eventContent={(arg) => {
              const time = arg.event.extendedProps?.formattedTime || '';
              const title = arg.event.title || '';
              return {
                html: `
                  <div style="
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    gap: 6px;
                  ">
                    <div style="font-weight:800; font-size:${slotTimeSize}; line-height:1; color:${arg.event.textColor};">
                      ${time}
                    </div>
                    <div style="font-weight:800; font-size:${slotTitleSize}; line-height:1; color:${arg.event.textColor};">
                      ${title}
                    </div>
                  </div>
                `,
              };
            }}
          />
        </Box>

        {/* Indicador móvil de scroll */}
        {isMobile && showScrollHelper && (
          <Paper
            elevation={4}
            sx={{
              position: 'fixed',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '10px 20px',
              borderRadius: '24px',
              backgroundColor: alpha(theme.palette.primary.main, 0.9),
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: '0 4px 15px rgba(0,0,0,0.25)',
              zIndex: 1000,
              animation: 'pulse 2s infinite'
            }}
          >
            <TouchAppIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Desliza para navegar
            </Typography>
          </Paper>
        )}

        {/* Diálogo de Booking */}
        <Dialog
          open={showBookingForm}
          onClose={(_, reason) => {
            if (isPaying && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
            handleBookingCancel();
          }}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          disableEscapeKeyDown={isPaying}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : '24px',
              overflow: 'hidden',
              boxShadow: '0 15px 50px rgba(0,0,0,0.2)',
            },
          }}
        >
          <DialogTitle
            component="div"
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: isMobile ? 2 : 2.5,
            }}
          >
            <Typography component="span" variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
              Agendar Cita
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => { if (!isPaying) handleBookingCancel(); }}
              disabled={isPaying}
              aria-label="cerrar"
              sx={{ backgroundColor: 'rgba(255,255,255,0.15)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' } }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: isMobile ? 3 : 4, mt: 1 }}>
            {selectedSlot && (
              <BookingForm
                doctorId={doctorId}
                selectedSlot={selectedSlot}
                onSuccess={handleBookingSuccess}
                onCancel={handleBookingCancel}
                onProcessingChange={setIsPaying}
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default PublicCalendar;
